import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/cn';
import { labelForPath, parentPathFor } from '@/nav/navigation';

/**
 * "Back a page", on every screen that has one.
 *
 * Two different journeys have to work and they disagree about what "back"
 * means. Someone who clicked in wants the page they came from; someone who
 * opened a pasted link has no history at all, and `navigate(-1)` would throw
 * them out of the app entirely. So history wins when the app put an entry
 * there, and the nav tree's parent is the fallback.
 *
 * React Router stamps `idx` on the history state for every in-app navigation.
 * `idx > 0` therefore means "there is one of our own pages behind this".
 */
export function BackLink({ className }: { className?: string }) {
  const navigate = useNavigate();
  const location = useLocation();

  const parent = parentPathFor(location.pathname);
  if (!parent) return null;

  const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
  const label = labelForPath(parent);

  return (
    <button
      type="button"
      onClick={() => (idx > 0 ? navigate(-1) : navigate(parent))}
      aria-label={`Back to ${label}`}
      title={`Back to ${label}`}
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-cloud hover:text-body',
        className,
      )}
    >
      <ChevronLeft aria-hidden className="h-5 w-5" />
    </button>
  );
}
